import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLocaleContext } from '../context/LocaleContext';
import blogMetadata from '../data/blogMetadata.json';
import { getLocalizedSlug } from '../blog/slugMapping';
import { BookOpen, Calendar, ArrowRight } from 'lucide-react';
import { BlogImage } from './BlogImage';

export const BlogModule: React.FC = () => {
  const { locale } = useLocaleContext();

  // Get the 6 most recent blog posts for the current locale from static metadata
  const recentPosts = useMemo(() => {
    const posts = blogMetadata[locale as keyof typeof blogMetadata] || blogMetadata.en;
    return posts.slice(0, 6);
  }, [locale]);

  // State for localized slugs
  const [localizedSlugs, setLocalizedSlugs] = useState<Record<string, string>>({});

  // Load localized slugs for all recent posts
  useEffect(() => {
    const loadSlugs = async () => {
      const slugMap: Record<string, string> = {};
      for (const post of recentPosts) {
        const localizedSlug = await getLocalizedSlug(post.slug, locale);
        slugMap[post.slug] = localizedSlug;
      }
      setLocalizedSlugs(slugMap);
    };

    if (recentPosts.length > 0) {
      loadSlugs();
    }
  }, [recentPosts, locale]);

  // Helper function to get the correct blog URL based on locale with localized slug
  const getBlogUrl = (englishSlug: string): string => {
    const localizedSlug = localizedSlugs[englishSlug] || englishSlug;

    // Construct the blog URL with the correct locale prefix
    if (locale === 'en') {
      return `/blog/${localizedSlug}`;
    } else if (locale === 'pt-BR') {
      return `/br/blog/${localizedSlug}`;
    } else if (locale === 'ms-MY') {
      return `/my/blog/${localizedSlug}`;
    } else if (locale === 'de') {
      return `/de/blog/${localizedSlug}`;
    } else if (locale === 'es') {
      return `/es/blog/${localizedSlug}`;
    } else if (locale === 'fr') {
      return `/fr/blog/${localizedSlug}`;
    } else if (locale === 'ja') {
      return `/ja/blog/${localizedSlug}`;
    } else if (locale === 'id') {
      return `/id/blog/${localizedSlug}`;
    }
    // Fallback to English
    return `/blog/${localizedSlug}`;
  };

  // Helper function to get the "View All" blog list URL
  const getBlogListUrl = (): string => {
    const localePrefixMap: Record<string, string> = {
      'en': '',
      'pt-BR': '/pt-br',
      'ms-MY': '/my',
      'es': '/es',
      'de': '/de',
      'fr': '/fr',
      'ja': '/ja',
      'id': '/id'
    };
    const localePrefix = localePrefixMap[locale] || '';
    return `${localePrefix}/blog`;
  };

  // Helper function to format date based on locale
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);

    const localeMap: Record<string, string> = {
      'en': 'en-US',
      'de': 'de-DE',
      'pt-BR': 'pt-BR',
      'ms-MY': 'ms-MY',
      'es': 'es-ES',
      'fr': 'fr-FR',
      'ja': 'ja-JP',
      'id': 'id-ID'
    };

    const browserLocale = localeMap[locale] || 'en-US';

    return date.toLocaleDateString(browserLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      sectionTitle: {
        en: 'Latest Articles',
        de: 'Neueste Artikel',
        'pt-BR': 'Artigos Recentes',
        'ms-MY': 'Artikel Terkini',
        es: 'Artículos Recientes',
        fr: 'Publications recentes',
        ja: '最新の記事',
        id: 'Artikel Terbaru'
      },
      sectionDescription: {
        en: 'Explore our latest guides on AI watermark detection, removal, and privacy.',
        de: 'Entdecken Sie unsere neuesten Anleitungen zur KI-Wasserzeichen-Erkennung, -Entfernung und zum Datenschutz.',
        'pt-BR': 'Explore nossos guias mais recentes sobre detecção, remoção e privacidade de marcas d\'água de IA.',
        'ms-MY': 'Terokai panduan terkini kami mengenai pengesanan, penyingkiran, dan privasi tanda air AI.',
        es: 'Explora nuestras últimas guías sobre detección, eliminación y privacidad de marcas de agua de IA.',
        fr: 'Decouvrez nos guides sur la detection et la suppression des filigranes IA, ainsi que sur la confidentialite.',
        ja: 'AI透かしの検出、削除、プライバシー保護に関する最新ガイドをご覧ください。',
        id: 'Jelajahi panduan terbaru kami tentang deteksi, penghapusan, dan privasi watermark AI.'
      },
      readMore: {
        en: 'Read more',
        de: 'Mehr lesen',
        'pt-BR': 'Leia mais',
        'ms-MY': 'Baca lagi',
        es: 'Leer más',
        fr: 'Lire la suite',
        ja: '続きを読む',
        id: 'Baca selengkapnya'
      },
      viewAll: {
        en: 'View all articles',
        de: 'Alle Artikel ansehen',
        'pt-BR': 'Ver todos os artigos',
        'ms-MY': 'Lihat semua artikel',
        es: 'Ver todos los artículos',
        fr: 'Voir tous les articles',
        ja: '全ての記事を見る',
        id: 'Lihat semua artikel'
      }
    };

    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  if (recentPosts.length === 0) {
    return null;
  }

  return (
    <div className="mt-20 mb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded-full mb-4">
            <BookOpen className="w-5 h-5" />
            <span className="font-semibold">Blog</span>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-charcoal-900 dark:text-white mb-4">
            {getText('sectionTitle')}
          </h2>

          <p className="text-lg text-neutral-600 dark:text-neutral-300 max-w-3xl mx-auto">
            {getText('sectionDescription')}
          </p>
        </div>

        {/* Blog Posts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {recentPosts.map((post) => (
            <Link
              key={post.slug}
              to={getBlogUrl(post.slug)}
              className="group bg-white dark:bg-charcoal-800 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-neutral-200 dark:border-charcoal-700 hover:border-accent-500 dark:hover:border-accent-500 hover:-translate-y-1"
            >
              {/* Cover Image */}
              <div className="relative overflow-hidden aspect-video">
                <BlogImage
                  coverImage={post.coverImage}
                  title={post.title}
                  variant="card"
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Date */}
                <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={post.date}>{formatDate(post.date)}</time>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-charcoal-900 dark:text-white mb-2 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-4 line-clamp-2">
                  {post.description}
                </p>

                {/* Read More - Unique per article for SEO */}
                <span className="inline-flex items-center gap-2 text-accent-600 dark:text-accent-300 font-semibold text-sm group-hover:gap-3 transition-all">
                  {getText('readMore')}: {post.title.length > 30 ? post.title.substring(0, 30) + '...' : post.title}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            to={getBlogListUrl()}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-accent-600 to-accent-700 hover:from-accent-700 hover:to-accent-800 text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            {getText('viewAll')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default BlogModule;
